/**
 * Modern Login Form Component for THPT App
 * Features: Email/password auth, Google SSO, password visibility toggle,
 * remember me, forgot password, smooth animations
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, Loader2, X } from 'lucide-react';

import { Button } from '@vsat/shared/components';
import { Input } from '@vsat/shared/components';
import { Checkbox } from '@vsat/shared/components';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@vsat/shared/components';
import { loginSchema, type LoginFormData } from '@vsat/shared/lib/auth/schemas';
import { GoogleIcon, FacebookIcon } from './icons';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { useNavigate } from 'react-router-dom';
import { getPostAuthRedirect } from '../../utils/authRedirect';

interface LoginFormProps {
  onSwitchToRegister?: () => void;
  onForgotPassword?: () => void;
  onSuccess?: (data: LoginFormData) => void;
  onNeedsVerification?: (email: string) => void;
}

export function LoginForm({
  onSwitchToRegister,
  onForgotPassword,
  onSuccess,
  onNeedsVerification,
}: LoginFormProps) {
  const navigate = useNavigate();
  const login = useAuthStore(state => state.login);
  const resendOtp = useAuthStore(state => state.resendOtp);
  const needsVerification = useAuthStore(state => state.needsVerification);
  const pendingIdentifier = useAuthStore(state => state.pendingIdentifier);
  const loginWithGoogle = useAuthStore(state => state.loginWithGoogle);
  const loginWithFacebook = useAuthStore(state => state.loginWithFacebook);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isFacebookLoading, setIsFacebookLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showUnverifiedError, setShowUnverifiedError] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  // Clear error message when user starts typing
  useEffect(() => {
    const subscription = form.watch((_value, { name }) => {
      if (name && errorMessage) {
        setErrorMessage(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [form, errorMessage]);

  const handleSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      await login(data.email, data.password);

      toast.success('Đăng nhập thành công!', {
        description: 'Chào mừng bạn trở lại THPT Online',
      });

      onSuccess?.(data);
      const destination = getPostAuthRedirect(window.location.search);
      navigate(destination, { replace: true });
    } catch (error: unknown) {
      console.error('Login error:', error);

      let errorMsg = '';
      if (error && typeof error === 'object') {
        if ('response' in error && error.response && typeof error.response === 'object') {
          const response = error.response as { data?: { message?: string } };
          errorMsg = response.data?.message || '';
        }
        if (!errorMsg && 'message' in error) {
          errorMsg = (error as { message: string }).message;
        }
      }

      if (errorMsg.includes('Invalid credentials') || errorMsg.includes('invalid credentials')) {
        setErrorMessage('Email hoặc mật khẩu không đúng');
        setShowUnverifiedError(false);
      } else if (errorMsg.includes('unverified') || errorMsg.includes('verify your account')) {
        setErrorMessage(
          'Tài khoản chưa được xác thực. Vui lòng xác thực email trước khi đăng nhập.'
        );
        setShowUnverifiedError(true);
      } else if (
        errorMsg.includes('Network') ||
        (error &&
          typeof error === 'object' &&
          'code' in error &&
          (error as { code: string }).code === 'NETWORK_ERROR')
      ) {
        setErrorMessage('Lỗi kết nối mạng. Vui lòng thử lại sau.');
        setShowUnverifiedError(false);
      } else {
        setErrorMessage('Đăng nhập thất bại. Vui lòng thử lại.');
        setShowUnverifiedError(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      toast.info('Đang kết nối với Google...');
      await loginWithGoogle();
    } catch (error) {
      toast.error('Đăng nhập bằng Google thất bại');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    setIsFacebookLoading(true);
    try {
      toast.info('Đang kết nối với Facebook...');
      await loginWithFacebook();
    } catch (error) {
      toast.error('Đăng nhập bằng Facebook thất bại');
    } finally {
      setIsFacebookLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingIdentifier) return;

    setIsResending(true);
    try {
      await resendOtp(pendingIdentifier);
      toast.success('Đã gửi lại mã OTP');

      if (onNeedsVerification) {
        onNeedsVerification(pendingIdentifier);
      }
    } catch (error: any) {
      console.error('Resend OTP error:', error);
      toast.error(error.message || 'Không thể gửi lại mã OTP');
    } finally {
      setIsResending(false);
    }
  };

  const handleGoToVerification = () => {
    if (onNeedsVerification && pendingIdentifier) {
      onNeedsVerification(pendingIdentifier);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className='w-full max-w-md space-y-6'
    >
      {/* Social SSO Buttons */}
      <div className='grid grid-cols-2 gap-3'>
        <Button
          type='button'
          variant='outline'
          className='w-full relative group'
          onClick={handleGoogleLogin}
          disabled={isGoogleLoading || isLoading || isFacebookLoading}
        >
          {isGoogleLoading ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <GoogleIcon className='h-4 w-4' />
          )}
          <span className='ml-2'>Google</span>
        </Button>

        <Button
          type='button'
          variant='outline'
          className='w-full relative group'
          onClick={handleFacebookLogin}
          disabled={isFacebookLoading || isLoading || isGoogleLoading}
        >
          {isFacebookLoading ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <FacebookIcon className='h-4 w-4' />
          )}
          <span className='ml-2'>Facebook</span>
        </Button>
      </div>

      {/* Divider */}
      <div className='relative'>
        <div className='absolute inset-0 flex items-center'>
          <div className='w-full border-t border-border' />
        </div>
        <div className='relative flex justify-center text-xs uppercase'>
          <span className='bg-background px-2 text-muted-foreground'>hoặc tiếp tục với</span>
        </div>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className='rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4'>
          <div className='flex items-start'>
            <div className='flex-1'>
              <p className='text-sm font-medium text-red-800 dark:text-red-200'>{errorMessage}</p>
              {showUnverifiedError && needsVerification && pendingIdentifier && (
                <div className='mt-3 flex flex-col sm:flex-row gap-2'>
                  <Button
                    type='button'
                    size='sm'
                    onClick={handleGoToVerification}
                    variant='outline'
                    className='border-red-300 dark:border-red-700 text-red-800 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/40'
                  >
                    Xác thực tài khoản
                  </Button>
                  <Button
                    type='button'
                    size='sm'
                    onClick={handleResendOtp}
                    disabled={isResending}
                    variant='outline'
                    className='border-red-300 dark:border-red-700 text-red-800 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/40'
                  >
                    {isResending ? (
                      <>
                        <Loader2 className='mr-2 h-3 w-3 animate-spin' />
                        Đang gửi...
                      </>
                    ) : (
                      'Gửi lại mã'
                    )}
                  </Button>
                </div>
              )}
            </div>
            <div className='ml-3 flex-shrink-0'>
              <button
                type='button'
                onClick={() => {
                  setErrorMessage(null);
                  setShowUnverifiedError(false);
                }}
                className='inline-flex rounded-md bg-red-50 dark:bg-red-900/20 p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50 dark:focus:ring-offset-red-900/20'
              >
                <span className='sr-only'>Đóng</span>
                <X className='h-3 w-3' aria-hidden='true' />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email/Password Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-4'>
          {/* Email Field */}
          <FormField
            control={form.control}
            name='email'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-sm font-medium text-foreground'>Email</FormLabel>
                <FormControl>
                  <div className='relative'>
                    <Mail className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                    <Input
                      type='email'
                      placeholder='email@example.com'
                      className='pl-10 border-border focus:border-primary focus:ring-2 focus:ring-primary/20'
                      {...field}
                      disabled={isLoading}
                      autoComplete='email'
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Password Field */}
          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-sm font-medium text-foreground'>Mật khẩu</FormLabel>
                <FormControl>
                  <div className='relative'>
                    <Lock className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder='••••••••'
                      className='pl-10 pr-10 border-border focus:border-primary focus:ring-2 focus:ring-primary/20'
                      {...field}
                      disabled={isLoading}
                      autoComplete='current-password'
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors'
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Remember Me & Forgot Password */}
          <div className='flex items-center justify-between'>
            <FormField
              control={form.control}
              name='rememberMe'
              render={({ field }) => (
                <FormItem className='flex items-center space-x-2 space-y-0'>
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className='border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary'
                    />
                  </FormControl>
                  <FormLabel className='text-sm font-normal text-foreground cursor-pointer'>
                    Ghi nhớ đăng nhập
                  </FormLabel>
                </FormItem>
              )}
            />

            <button
              type='button'
              onClick={onForgotPassword}
              className='text-sm font-medium text-primary hover:text-primary/80 transition-colors'
            >
              Quên mật khẩu?
            </button>
          </div>

          {/* Submit Button */}
          <Button
            type='submit'
            className='w-full bg-primary hover:bg-primary/90 text-primary-foreground'
            disabled={isLoading || isGoogleLoading || isFacebookLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Đang đăng nhập...
              </>
            ) : (
              'Đăng nhập'
            )}
          </Button>
        </form>
      </Form>

      {/* Switch to Register */}
      <div className='text-center text-sm'>
        <span className='text-muted-foreground'>Chưa có tài khoản? </span>
        <button
          onClick={onSwitchToRegister}
          className='font-medium text-primary hover:text-primary/80 transition-colors'
        >
          Đăng ký ngay
        </button>
      </div>
    </motion.div>
  );
}
